import dayjs from 'dayjs';
import {
  BuyOrderApiRelation,
  BuyOrderApiResponse,
} from '../../../components/admin/buy-process/buy-order-detailed/buy-order-detailed-types';
import {
  InvoiceRecipient,
  RelationType,
  SignAuthority,
} from '../../../global/entities/BuyOrder';

export function extractClientInfo(response: BuyOrderApiResponse) {
  function isContactPersonRelation(relation: BuyOrderApiRelation) {
    return relation.relationType === RelationType.ContactPerson;
  }

  // There is always a client in an order, so we can safely non-null assert
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const clientRelation = response.relations.find(isContactPersonRelation)!;

  const relationProperties = {
    email: clientRelation.email,
    emailRepeat: clientRelation.email,
    organizationName: clientRelation.organizationName,
    phoneNumber: clientRelation.phoneNumber,
    relationId: clientRelation.id,
    relationType: RelationType.ContactPerson,
  };

  /*
   * If the person doesn't have an SSN, we don't want
   * to create an entity. So we just inline the properties
   * in the store.
   * */
  if (!clientRelation.person.socialSecurityNumber) {
    return {
      ...relationProperties,
      firstName: clientRelation.person.firstName,
      lastName: clientRelation.person.lastName,
      address: clientRelation.person.address,
      postalCode: clientRelation.person.postalCode,
      city: clientRelation.person.city,
    };
  }

  return {
    ...relationProperties,
    person: clientRelation.person.socialSecurityNumber,
  };
}

export function extractCompanyDetails(response: BuyOrderApiResponse) {
  return {
    id: response.company.id,
    orderId: response.id,
    desiredCompanyName: response.company.desiredCompanyName,
    firstAlternativeCompanyName: response.company.firstAlternativeCompanyName,
    secondAlternativeCompanyName: response.company.secondAlternativeCompanyName,
    nameReview: response.company.nameReview,
    municipality: response.company.municipality,
    businessDescription: response.company.businessDescription,
    taxHelp: response.company.taxHelp,
    co: response.company.co,
    address: response.company.address,
    postalCode: response.company.postalCode,
    city: response.company.city,
    // This is not stored on the backend, so we can't know
    // which template was selected
    templateSelect: '',
  };
}

export function extractOwnerShares(response: BuyOrderApiResponse) {
  const physicalPeople = response.relations
    .filter(
      (relation) => relation.relationType === RelationType.PhysicalShareHolder,
    )
    .map((relation) => ({
      numberShares: relation.numberShares ?? 0,
      relationType: relation.relationType,
      person: relation.person.socialSecurityNumber,
      relationId: relation.id,
    }));

  const legalShareholders = response.organizations.map((organization) => ({
    numberShares: organization.numberShares ?? 0,
    organization: organization.number,
    relationType: RelationType.LegalShareHolder,
    person: organization.signers[0].person.socialSecurityNumber,
  }));

  return {
    shareCapital: response.shareCapital,
    beneficialOwnerHelp: response.beneficialOwnerHelp,
    numberShares: response.numberShares,
    legalShareholders,
    physicalPeople,
  };
}
export function extractRepresentatives(response: BuyOrderApiResponse) {
  const isRepresentative = (relation: BuyOrderApiResponse['relations'][0]) =>
    // eslint-disable-next-line no-bitwise
    relation.relationType & RelationType.Representatives;

  const representatives = response.relations
    .filter(isRepresentative)
    .map((relation) => ({
      relationType: relation.relationType,
      person: relation.person.socialSecurityNumber,
      relationId: relation.id,
    }));

  const ceoRelation = response.relations.find(
    (relation) => relation.relationType === RelationType.Ceo,
  );

  const ceo = ceoRelation && {
    relationType: ceoRelation.relationType,
    person: ceoRelation.person.socialSecurityNumber,
    relationId: ceoRelation.id,
  };

  const isPreDefined = (
    signAuthority: string | SignAuthority,
  ): signAuthority is SignAuthority =>
    signAuthority === SignAuthority.AB1
    || signAuthority === SignAuthority.AB2
    || signAuthority === SignAuthority.AB3
    || signAuthority === SignAuthority.AB4
    || signAuthority === SignAuthority.AB5;

  return {
    officialCeo: response.officialCeo,
    ceo,
    signAuthority: isPreDefined(response.signAuthority)
      ? response.signAuthority
      : SignAuthority.ABX,
    // If its not pre-defined, then its user defined
    signAuthorityUserDefined: !isPreDefined(response.signAuthority)
      ? response.signAuthority
      : '',
    representatives,
  };
}
export function extractFiscalYear(response: BuyOrderApiResponse) {
  const endMonth = response.fiscalYear.slice(2);
  const currentMonth = dayjs().format('MM');
  const closingDate = dayjs()
    .month(Number(endMonth) - 1)
    .add(currentMonth >= endMonth ? 1 : 0, 'year')
    .endOf('month')
    .format('YYYY-MM-DD');

  /*
    firstClosingDate will always be present for the new orders.
    This logic is for handling the legacy orders.
  */
  const firstClosingDate = response.firstClosingDate
    ? dayjs(response.firstClosingDate).format('YYYY-MM-DD')
    : closingDate;

  return {
    firstClosingDate,
    extendedFiscalYear: response.extendedFiscalYear,
    fiscalYear: response.fiscalYear,
  };
}

export function extractAuditor(response: BuyOrderApiResponse) {
  if (!response.auditor) return { auditorOption: 'no' as const };

  const hasAuditorOrganization = response.auditor.organizationAddress != undefined;

  if (hasAuditorOrganization) {
    return {
      // Assert non-nulls because if there is an auditor organization,
      // then these properties *must* be defined on the response.
      auditorFirm: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        organizationName: response.auditor.organizationName!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        organizationNumber: response.auditor.organizationNumber!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        organizationAddress: response.auditor.organizationAddress!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        organizationPostalCode: response.auditor.organizationPostalCode!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        organizationCity: response.auditor.organizationCity!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      },
      auditorPerson: {
        person: response.auditor.socialSecurityNumber,
      },
      auditorOption: 'company' as const,
      id: response.auditor.id,
    };
  }

  // At this point, its a person auditor by exhaustiveness
  return {
    auditorPerson: {
      person: response.auditor.socialSecurityNumber,
    },
    auditorOption: 'person' as const,
    id: response.auditor.id,
  };
}

export function extractProxies(response: BuyOrderApiResponse) {
  // There is always a power of attorney in an order
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const powerOfAttorney = response.relations.find(
    (relation) => relation.relationType === RelationType.PowerOfAttorney,
  )!;

  return {
    relationType: powerOfAttorney.relationType,
    relationId: powerOfAttorney.id,
    person: powerOfAttorney.person.socialSecurityNumber,
    bankAccountHelp: response.bankAccountHelp ?? false,
  };
}

export function extractReservation(response: BuyOrderApiResponse) {
  return {
    stockRestrictions: response.stockRestrictions,
    rightFirstRefusal: response.rightFirstRefusal,
  };
}

export function extractMetadata(response: BuyOrderApiResponse) {
  return {
    internalDealDate: response.internalDealDate,
    internalManagerId: response.internalManagerId,
    id: response.id,
    registrationDate: response.registrationDate,
    companyNumber: response.companyNumber,
    organizationNumber: response.organizationNumber,
    organizationName: response.organizationName,
    invoiceNumber: response.invoiceNumber,
    files: response.files,

    ...(response.internalManager && {
      internalManager: {
        id: response.internalManager.id,
        userId: response.internalManager.userId,
      },
    }),
  };
}

export const mapBuyOrderResponse = (response: BuyOrderApiResponse) => ({
  clientInfo: extractClientInfo(response),
  reservation: extractReservation(response),
  proxies: extractProxies(response),
  companyDetails: extractCompanyDetails(response),
  ownerShares: extractOwnerShares(response),
  representatives: extractRepresentatives(response),
  fiscalYear: extractFiscalYear(response),
  auditor: extractAuditor(response),
  costStatement: {
    receiveOption: response.digitalCustomerSignature
      ? ('email' as const)
      : ('mail' as const),
    digitalCustomerSignature: response.digitalCustomerSignature,
    receiverEmails: Boolean(response.emails) ? JSON.parse(response.emails) : [],
    invoiceRecipient: String(response.invoiceRecipient) as InvoiceRecipient,
    manualHandling: response.manualHandling,
    taxAccountOcr: response.taxAccountOcr ?? '',
    totalPayment: {
      vat: 0,
      registrationCost: 0,
      administrationCost: 0,
      beneficialCost: 0,
      nameReviewCost: 0,
      taxHelpCost: 0,
      totalPayment: 0,
    },
  },
  metadata: extractMetadata(response),
});
